/**
 * Reveal a secret value (ISS-473).
 *
 * Requires Secret#view_metadata on the backend — tool-injection (`use`) grants never
 * allow revealing. The reveal is rate-limited and audited server-side. The
 * value is shown once here, can be copied, and is never sent to the LLM.
 */

import { FormEvent, useEffect, useState } from 'react';
import { Copy, Eye, EyeOff } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useI18n } from '@/i18n';
import { useRevealSecret } from '../hooks/use-secrets';
import type { Secret } from '../types';

interface Props {
  open: boolean;
  onClose: () => void;
  secret: Secret | null;
}

export function SecretRevealModal({ open, onClose, secret }: Props) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [value, setValue] = useState<string | null>(null);
  const [showValue, setShowValue] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reveal = useRevealSecret();

  useEffect(() => {
    if (!open) {
      setReason('');
      setValue(null);
      setShowValue(false);
      setError(null);
    }
  }, [open]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!secret) return;
    setError(null);
    try {
      const revealed = await reveal.mutateAsync({ id: secret.id, reason });
      setValue(revealed);
      setShowValue(true);
    } catch {
      setError(
        t('admin.secrets.reveal.denied') ||
          'You do not have permission to reveal this secret, or the rate limit was reached.',
      );
    }
  }

  async function handleCopy() {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    toast.success(t('admin.secrets.reveal.copied') || 'Copied to clipboard');
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('admin.secrets.reveal.title') || 'Reveal secret value'}
          </DialogTitle>
          <DialogDescription>
            {t('admin.secrets.reveal.description') ||
              'The decrypted value is shown once and is never visible to AI models. This action is audited.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          {secret && (
            <p className="text-sm font-medium">
              {secret.name}
            </p>
          )}
          {value === null ? (
            <>
              <div>
                <Label htmlFor="reveal-reason">
                  {t('admin.secrets.reveal.reason') || 'Reason (optional)'}
                </Label>
                <Input
                  id="reveal-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={
                    t('admin.secrets.reveal.reasonPlaceholder') ||
                    'e.g. deploying to CI pipeline'
                  }
                />
              </div>
              {error && (
                <p className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              )}
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={onClose}>
                  {t('common.cancel') || 'Cancel'}
                </Button>
                <Button type="submit" disabled={reveal.isPending}>
                  {reveal.isPending
                    ? t('admin.secrets.reveal.loading') || 'Revealing…'
                    : t('admin.secrets.reveal.submit') || 'Reveal'}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  type={showValue ? 'text' : 'password'}
                  value={value}
                  className="font-mono"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowValue((prev) => !prev)}
                  aria-label={showValue ? 'Hide value' : 'Show value'}
                >
                  {showValue ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleCopy}
                  aria-label={t('admin.secrets.reveal.copy') || 'Copy'}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('admin.secrets.reveal.auditNote') ||
                  'This reveal has been recorded in the audit log.'}
              </p>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={onClose}>
                  {t('common.close') || 'Close'}
                </Button>
              </DialogFooter>
            </>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
